/**
 * OfflineSyncManager — MobX store для управління чергою офлайн-мутацій
 *
 * Відповідає за:
 * - Накопичення мутацій у IndexedDB під час офлайн-режиму
 * - Синхронізацію черги при відновленні з'єднання
 * - Відображення стану синхронізації в UI (pendingCount, isSyncing, failedMutations)
 * - Можливість повторити або відхилити помилкові мутації
 */

import { makeAutoObservable, runInAction } from 'mobx'
import { getAllMutations, addMutation, updateMutation, deleteMutation, type OfflineMutation } from './OfflineDB'
import { isDuplicateStatusChange } from './queueDedup'
import { restClient } from '@/Core'
import { parseApiErrorDescription } from '@/Ship/Helpers/parseApiError'

/**
 * Human-readable fallback message per mutation type, used when the backend
 * error payload carries no `message`/`violations` (e.g. network-level failure).
 * Keeps the driver-facing alert free of raw ky/HTTP technical text.
 */
function fallbackErrorMessage(mutation: OfflineMutation): string {
    switch (mutation.type) {
        case 'status-change':
            return 'Не вдалось змінити статус завдання'
        case 'delay-report':
            return 'Не вдалось надіслати звіт про затримку'
        case 'complete-task':
            return 'Не вдалось завершити завдання'
        default:
            return 'Не вдалось синхронізувати дію'
    }
}

class OfflineSyncManager {
    /** Кількість мутацій у стані 'pending' */
    pendingCount: number = 0

    /** Чи зараз виконується синхронізація */
    isSyncing: boolean = false

    /** Мутації, які завершились помилкою */
    failedMutations: OfflineMutation[] = []

    /** Слухачі, які викликаються після завершення sync() — для оновлення UI стану. */
    private _onSyncFinishedListeners: Array<() => void> = []

    constructor() {
        makeAutoObservable(this)
        this._loadState()
    }

    /** Підписатись на подію "очередь синхронізована" (повертає функцію відписки). */
    onSyncFinished(cb: () => void): () => void {
        this._onSyncFinishedListeners.push(cb)
        return () => {
            this._onSyncFinishedListeners = this._onSyncFinishedListeners.filter((x) => x !== cb)
        }
    }

    // ── State loading ─────────────────────────────────────────────────────────

    private async _loadState(): Promise<void> {
        try {
            const all = await getAllMutations()
            runInAction(() => {
                this.pendingCount = all.filter((m) => m.status === 'pending').length
                this.failedMutations = all.filter((m) => m.status === 'failed')
            })
        } catch (e) {
            console.error('[OfflineSyncManager] Failed to load state from IDB', e)
        }
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Додає мутацію до черги IndexedDB.
     * Викликається коли HTTP-запит не може бути виконаний (офлайн).
     */
    async enqueue(mutation: Omit<OfflineMutation, 'id' | 'enqueuedAt' | 'retryCount' | 'status'>): Promise<void> {
        try {
            // Захист від подвійного натискання: та сама зміна статусу одразу поспіль
            if (mutation.type === 'status-change' && isDuplicateStatusChange(await getAllMutations(), mutation)) {
                return
            }

            await addMutation({
                ...mutation,
                enqueuedAt: Date.now(),
                retryCount: 0,
                status: 'pending',
            })
            runInAction(() => {
                this.pendingCount++
            })
        } catch (e) {
            console.error('[OfflineSyncManager] Failed to enqueue mutation', e)
            throw e
        }
    }

    /**
     * Обробляє всі pending-мутації у черзі.
     * Викликається автоматично при відновленні з'єднання через useSyncOnReconnect.
     */
    async sync(): Promise<void> {
        if (this.isSyncing) return

        runInAction(() => {
            this.isSyncing = true
        })

        try {
            const all = await getAllMutations()
            const pending = all.filter((m) => m.status === 'pending')

            for (const mutation of pending) {
                await this._processMutation(mutation)
            }
        } catch (e) {
            console.error('[OfflineSyncManager] Sync error', e)
        } finally {
            await this._loadState()
            runInAction(() => {
                this.isSyncing = false
            })
            for (const cb of this._onSyncFinishedListeners) {
                try {
                    cb()
                } catch (e) {
                    console.error('[OfflineSyncManager] listener error', e)
                }
            }
        }
    }

    /**
     * Повторити всі невдалі мутації в тому самому порядку, що й початкова черга.
     * Індивідуальний повтор навмисно відсутній: зміни статусів мають застосовуватись
     * строго послідовно, тому повтор завжди виконується пакетом.
     */
    async retryAllFailed(): Promise<void> {
        const all = await getAllMutations()
        const failed = all.filter((m) => m.status === 'failed')
        await Promise.all(failed.map((m) => updateMutation({ ...m, status: 'pending', lastError: undefined })))
        await this._loadState()
        await this.sync()
    }

    /**
     * Відхилити всі невдалі мутації (видалити з черги без повтору).
     */
    async discardAllFailed(): Promise<void> {
        const all = await getAllMutations()
        const failed = all.filter((m) => m.status === 'failed')
        await Promise.all(failed.map((m) => deleteMutation(m.id!)))
        await this._loadState()
    }

    /**
     * Відхилити одну конкретну невдалу мутацію.
     * Увага: для мутацій типу status-change відхилення однієї може зробити
     * подальші мутації некоректними — використовуйте з обережністю.
     */
    async discardFailed(id: number): Promise<void> {
        await deleteMutation(id)
        await this._loadState()
    }

    // ── Private ───────────────────────────────────────────────────────────────

    private async _processMutation(mutation: OfflineMutation): Promise<void> {
        // Момент дії на пристрої: бекенд приймає офлайн-дію, якщо тоді тривала зміна водія
        // (навіть уже завершена), і позначає її в історії як офлайнову
        const offlineAt = new Date(mutation.enqueuedAt).toISOString()
        try {
            if (mutation.type === 'status-change') {
                await restClient.post(`/services/executor-assignments/${mutation.uuid}/change-status`, {
                    json: { status: mutation.newStatus, offlineAt, confirmedAt: mutation.confirmedAt },
                })
            } else if (mutation.type === 'delay-report') {
                const photoUrls: string[] = []
                for (const [index, blob] of (mutation.photos ?? []).entries()) {
                    // Derive a filename so multipart carries the original extension.
                    const ext = (blob.type?.split('/')[1] || 'jpg').replace('jpeg', 'jpg')
                    const fileName = `delay-${mutation.uuid}-${index}.${ext}`
                    const formData = new FormData()
                    formData.append('file', blob, fileName)
                    formData.append('name', fileName)
                    // `isNotHeaderAddContentType: true` is critical: otherwise the REST
                    // client forces `application/ld+json` and the multipart boundary is
                    // lost, so the backend receives no file and answers 422.
                    const result = (await restClient.post(
                        '/services/user/photo_files',
                        { body: formData },
                        { isNotHeaderAddContentType: true }
                    )) as any
                    if (result?.url) photoUrls.push(result.url)
                }
                await restClient.post(`/services/executor-assignments/${mutation.uuid}/delays`, {
                    json: { driverComment: mutation.driverComment, photoUrls, offlineAt },
                })
            } else if (mutation.type === 'complete-task') {
                for (const row of mutation.ratings ?? []) {
                    await restClient.post(`/services/executor-assignments/${mutation.uuid}/ratings`, {
                        json: { ...row, offlineAt },
                    })
                }
                await restClient.post(`/services/executor-assignments/${mutation.uuid}/change-status`, {
                    json: { status: 'completed_by_driver', offlineAt, confirmedAt: mutation.confirmedAt },
                })
            }

            // Success — remove from queue
            if (mutation.id !== undefined) {
                await deleteMutation(mutation.id)
            }
        } catch (error: any) {
            // Raw `error.message` is ky technical text ("Request failed with status
            // code 400: POST https://..."). Extract the backend's human message
            // (or a type-specific fallback) so the driver sees something readable.
            const lastError = await parseApiErrorDescription(error, fallbackErrorMessage(mutation))
            console.error('[OfflineSyncManager] Mutation failed', { mutation, error })
            if (mutation.id !== undefined) {
                await updateMutation({
                    ...mutation,
                    status: 'failed',
                    retryCount: mutation.retryCount + 1,
                    lastError,
                })
            }
        }
    }
}

export const offlineSyncManager = new OfflineSyncManager()
